/**
 * 空状態の手順・サンプル CTA 帯（デスクトップ中央ペイン上部）
 */

import { Fragment, type ReactNode } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { StandardDatasetEntry } from '../../../../services/systems/keisuke/keisukeStandardDatasets';
import {
  KeisukePrimarySampleButton,
  KeisukeSampleDataDropdown,
} from './KeisukeDashboardCenterToolbarBits';
import styles from './KeisukeDashboardCenterPane.module.scss';

export type KeisukeDashboardEmptyHeroBandProps = {
  primarySample: StandardDatasetEntry | null;
  hasAnySample: boolean;
  tutorialForType: StandardDatasetEntry[];
  verificationForType: StandardDatasetEntry[];
  onLoadPrimarySample: () => void;
  onLoadSampleData: (entry: StandardDatasetEntry) => void;
  onImportClick: () => void;
  onPasteFromClipboard: () => void;
};

export default function KeisukeDashboardEmptyHeroBand({
  primarySample,
  hasAnySample,
  tutorialForType,
  verificationForType,
  onLoadPrimarySample,
  onLoadSampleData,
  onImportClick,
  onPasteFromClipboard,
}: KeisukeDashboardEmptyHeroBandProps) {
  const { t } = useTranslation();

  const secondaryActions: Array<{ key: string; node: ReactNode }> = [
    {
      key: 'import',
      node: (
        <Button
          size="sm"
          variant="link"
          className={styles.emptyHeroSecondaryBtn}
          onClick={onImportClick}
          title={t('keisuke.dashboard.tooltips.importData')}
        >
          <i className="fas fa-file-import" aria-hidden />
          {t('keisuke.dashboard.toolbar.import')}
        </Button>
      ),
    },
    {
      key: 'paste',
      node: (
        <Button
          size="sm"
          variant="link"
          className={styles.emptyHeroSecondaryBtn}
          onClick={onPasteFromClipboard}
          title={t('keisuke.dashboard.tooltips.paste')}
        >
          <i className="fas fa-paste" aria-hidden />
          {t('keisuke.dashboard.toolbar.paste')}
        </Button>
      ),
    },
  ];

  if (hasAnySample) {
    secondaryActions.push({
      key: 'samples',
      node: (
        <KeisukeSampleDataDropdown
          id="keisuke-sample-data-dropdown-empty"
          align="start"
          variant="link"
          toggleClassName={styles.emptyHeroSecondaryBtn}
          tutorialForType={tutorialForType}
          verificationForType={verificationForType}
          hasAnySample={hasAnySample}
          onLoadSampleData={onLoadSampleData}
          excludeSampleId={primarySample?.id}
        />
      ),
    });
  }

  return (
    <div className={`${styles.emptyHero} ${styles.emptyHeroBand}`}>
      {/* 主CTAを先に置き、手順は補助テキスト（視線が緑ボタンから入る） */}
      <div className={styles.emptyHeroActions}>
        {primarySample ? (
          <div className={styles.emptyHeroPrimary}>
            <KeisukePrimarySampleButton
              onClick={onLoadPrimarySample}
              title={t(primarySample.nameKey)}
            />
          </div>
        ) : null}
        <div className={styles.emptyHeroSecondary}>
          {secondaryActions.map((action, index) => (
            <Fragment key={action.key}>
              {index > 0 ? (
                <span className={styles.emptyHeroSecondarySep} aria-hidden>
                  ·
                </span>
              ) : null}
              {action.node}
            </Fragment>
          ))}
        </div>
      </div>
      {/* 右ドックが「結果の置き場」を案内するため、サンプルあり時は手順文を重ねない */}
      {!primarySample ? (
        <p className={styles.emptyHeroSteps}>
          {t('keisuke.workspace.emptyHeroStepsNoSample')}
        </p>
      ) : null}
    </div>
  );
}
